import { toSafeString } from './toSafeString.js';
const SUPPORTED_PROVIDERS = new Set(['openai', 'anthropic', 'gemini']);

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp']);

const OPENAI_DOCUMENT_MIME_TYPES = new Set(['application/pdf', 'text/plain', 'text/markdown', 'text/csv', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.openxmlformats-officedocument.presentationml.presentation']);

const ANTHROPIC_DOCUMENT_MIME_TYPES = new Set(['application/pdf', 'text/plain']);
const GEMINI_DOCUMENT_MIME_TYPES = new Set(['application/pdf', 'text/plain']);

const CONVERSION_METHOD_BY_MIME_TYPE = Object.freeze({
  'application/json': 'pass_through',
  'application/javascript': 'pass_through',
  'application/xml': 'pass_through',
  'application/x-javascript': 'pass_through',
  'application/x-python-code': 'pass_through',
  'application/x-sh': 'pass_through',
  'application/yaml': 'pass_through',
  'application/x-yaml': 'pass_through',
  'text/csv': 'pass_through',
  'text/html': 'html_strip',
  'text/javascript': 'pass_through',
  'text/markdown': 'pass_through',
  'text/plain': 'pass_through',
  'text/rtf': 'rtf_strip',
  'text/tab-separated-values': 'pass_through',
  'text/x-c': 'pass_through',
  'text/x-c++': 'pass_through',
  'text/x-java-source': 'pass_through',
  'text/x-markdown': 'pass_through',
  'text/x-python': 'pass_through',
  'text/x-shellscript': 'pass_through',
  'text/x-typescript': 'pass_through',
  'text/xml': 'pass_through'
});

const CONVERSION_METHOD_BY_EXTENSION = Object.freeze({
  c: 'pass_through',
  cc: 'pass_through',
  cpp: 'pass_through',
  cs: 'pass_through',
  css: 'pass_through',
  csv: 'pass_through',
  go: 'pass_through',
  h: 'pass_through',
  hh: 'pass_through',
  hpp: 'pass_through',
  htm: 'html_strip',
  html: 'html_strip',
  ini: 'pass_through',
  java: 'pass_through',
  js: 'pass_through',
  json: 'pass_through',
  jsx: 'pass_through',
  md: 'pass_through',
  markdown: 'pass_through',
  mjs: 'pass_through',
  py: 'pass_through',
  rb: 'pass_through',
  rtf: 'rtf_strip',
  sh: 'pass_through',
  sql: 'pass_through',
  text: 'pass_through',
  toml: 'pass_through',
  ts: 'pass_through',
  tsv: 'pass_through',
  tsx: 'pass_through',
  txt: 'pass_through',
  xml: 'pass_through',
  yaml: 'pass_through',
  yml: 'pass_through'
});

const FORMAT_LABEL_BY_EXTENSION = Object.freeze({
  csv: 'CSV',
  docx: 'DOCX',
  htm: 'HTML',
  html: 'HTML',
  md: 'Markdown',
  markdown: 'Markdown',
  py: 'Python',
  rtf: 'RTF',
  tsv: 'TSV',
  txt: 'Text',
  xlsx: 'XLSX',
  xml: 'XML',
  yaml: 'YAML',
  yml: 'YAML'
});

const FORMAT_LABEL_BY_MIME_TYPE = Object.freeze({
  'application/json': 'JSON',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
  'application/xml': 'XML',
  'text/csv': 'CSV',
  'text/html': 'HTML',
  'text/markdown': 'Markdown',
  'text/rtf': 'RTF',
  'text/tab-separated-values': 'TSV',
  'text/x-markdown': 'Markdown',
  'text/xml': 'XML'
});

const normalizeMimeType = (mimeType) => {
  const raw = toSafeString(mimeType).toLowerCase();

  if (!raw) {
    return '';
  }

  const [baseType] = raw.split(';');
  return toSafeString(baseType).toLowerCase();
};

const normalizeProvider = (provider) => {
  const normalized = toSafeString(provider).toLowerCase();

  if (SUPPORTED_PROVIDERS.has(normalized)) {
    return normalized;
  }

  return 'openai';
};

const normalizeMediaKind = (value) => {
  const normalized = toSafeString(value).toLowerCase();

  if (normalized === 'image' || normalized === 'document') {
    return normalized;
  }

  return 'document';
};

const normalizeFileExtension = (filename) => {
  const safeFilename = toSafeString(filename).toLowerCase();

  if (!safeFilename || !safeFilename.includes('.')) {
    return '';
  }

  return toSafeString(safeFilename.split('.').pop()).toLowerCase();
};

const resolveProviderDocumentMimeTypes = (provider) => {
  const safeProvider = normalizeProvider(provider);

  if (safeProvider === 'anthropic') {
    return ANTHROPIC_DOCUMENT_MIME_TYPES;
  }

  if (safeProvider === 'gemini') {
    return GEMINI_DOCUMENT_MIME_TYPES;
  }

  return OPENAI_DOCUMENT_MIME_TYPES;
};

const resolveFormatLabel = ({ mimeType, filename } = {}) => {
  const safeMimeType = normalizeMimeType(mimeType);
  const extension = normalizeFileExtension(filename);

  if (extension && FORMAT_LABEL_BY_EXTENSION[extension]) {
    return FORMAT_LABEL_BY_EXTENSION[extension];
  }

  if (safeMimeType && FORMAT_LABEL_BY_MIME_TYPE[safeMimeType]) {
    return FORMAT_LABEL_BY_MIME_TYPE[safeMimeType];
  }

  if (extension) {
    return extension.toUpperCase();
  }

  if (safeMimeType.includes('/')) {
    return safeMimeType.split('/').pop().toUpperCase();
  }

  return 'file';
};

const resolveConversionMethod = ({ mimeType, filename } = {}) => {
  const safeMimeType = normalizeMimeType(mimeType);
  const extension = normalizeFileExtension(filename);

  if (safeMimeType && CONVERSION_METHOD_BY_MIME_TYPE[safeMimeType]) {
    return CONVERSION_METHOD_BY_MIME_TYPE[safeMimeType];
  }

  if (extension && CONVERSION_METHOD_BY_EXTENSION[extension]) {
    return CONVERSION_METHOD_BY_EXTENSION[extension];
  }

  if (safeMimeType.startsWith('text/')) {
    return 'pass_through';
  }

  return null;
};

const buildConversionNotice = ({ sourceFormatLabel } = {}) => {
  const sourceFormat = toSafeString(sourceFormatLabel) || 'this file format';
  return `Note: This file was converted from ${sourceFormat} to plain text. Formatting may have been lost.`;
};

const resolveFilePolicy = ({ provider, mediaKind, mimeType, filename } = {}) => {
  const safeProvider = normalizeProvider(provider);
  const safeMediaKind = normalizeMediaKind(mediaKind);
  const safeMimeType = normalizeMimeType(mimeType);
  const conversionMethod = resolveConversionMethod({ mimeType: safeMimeType, filename });
  const shouldConvertToText = Boolean(conversionMethod);
  const sourceFormatLabel = resolveFormatLabel({
    mimeType: safeMimeType,
    filename
  });

  if (safeMediaKind === 'image') {
    return {
      provider: safeProvider,
      mediaKind: safeMediaKind,
      mimeType: safeMimeType,
      isProviderSupported: IMAGE_MIME_TYPES.has(safeMimeType),
      shouldConvertToText: false,
      conversionMethod: null,
      sourceFormatLabel,
      conversionNotice: '',
      targetMimeType: null
    };
  }

  const providerDocumentMimeTypes = resolveProviderDocumentMimeTypes(safeProvider);
  const isProviderSupported = providerDocumentMimeTypes.has(safeMimeType);

  if (shouldConvertToText) {
    return {
      provider: safeProvider,
      mediaKind: safeMediaKind,
      mimeType: safeMimeType,
      isProviderSupported,
      shouldConvertToText: true,
      conversionMethod,
      sourceFormatLabel,
      conversionNotice: conversionMethod === 'pass_through' ? '' : buildConversionNotice({ sourceFormatLabel }),
      targetMimeType: 'text/plain'
    };
  }

  if (isProviderSupported) {
    return {
      provider: safeProvider,
      mediaKind: safeMediaKind,
      mimeType: safeMimeType,
      isProviderSupported: true,
      shouldConvertToText: false,
      conversionMethod: null,
      sourceFormatLabel,
      conversionNotice: '',
      targetMimeType: null
    };
  }

  return {
    provider: safeProvider,
    mediaKind: safeMediaKind,
    mimeType: safeMimeType,
    isProviderSupported: false,
    shouldConvertToText,
    conversionMethod,
    sourceFormatLabel,
    conversionNotice: shouldConvertToText ? buildConversionNotice({ sourceFormatLabel }) : '',
    targetMimeType: shouldConvertToText ? 'text/plain' : null
  };
};

export { IMAGE_MIME_TYPES, OPENAI_DOCUMENT_MIME_TYPES, ANTHROPIC_DOCUMENT_MIME_TYPES, GEMINI_DOCUMENT_MIME_TYPES, normalizeMimeType as normalizePieveMultimodalMimeType, normalizeProvider as normalizePieveMultimodalProvider, normalizeMediaKind as normalizePieveMultimodalMediaKind, normalizeFileExtension as normalizePieveMultimodalFileExtension, resolveProviderDocumentMimeTypes as resolvePieveProviderDocumentMimeTypes, resolveFormatLabel as resolvePieveMultimodalFormatLabel, resolveConversionMethod as resolvePieveMultimodalConversionMethod, buildConversionNotice as buildPieveMultimodalConversionNotice, resolveFilePolicy as resolvePieveMultimodalFilePolicy };
